import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // null = checking, false = not auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        // Try refresh
        const refresh = await fetch(`${API_BASE}/api/auth/refresh`, {
          method: 'POST', credentials: 'include'
        });
        if (refresh.ok) {
          const data = await refresh.json();
          setUser(data);
        } else {
          setUser(false);
        }
      }
    } catch {
      setUser(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Error de autenticacion');
    }
    const data = await res.json();
    setUser(data);
    return data;
  };

  const logout = async () => {
    await fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST', credentials: 'include'
    }).catch(() => {});
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        return data;
      }
    } catch {}
    return null;
  };

  const impersonate = async (userId) => {
    const res = await fetch(`${API_BASE}/api/users/${userId}/impersonate`, {
      method: 'POST', credentials: 'include'
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Error al impersonar');
    }
    const data = await res.json();
    setUser(data);
    return data;
  };

  const stopImpersonation = async () => {
    const res = await fetch(`${API_BASE}/api/users/stop-impersonation`, {
      method: 'POST', credentials: 'include'
    });
    if (!res.ok) throw new Error('Error al detener impersonacion');
    const data = await res.json();
    setUser(data);
    return data;
  };

  const isAdmin = user && user.role === 'ADMIN';
  const isCompanyAdmin = user && user.role === 'COMPANY_ADMIN';
  const isImpersonating = user && user._impersonator_id;

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, refreshUser,
      impersonate, stopImpersonation,
      isAdmin, isCompanyAdmin, isImpersonating,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
